import { useState, useEffect, useCallback } from 'react';
import { Plus, Search, CreditCard, Eye, XCircle, Wallet, ArrowDownCircle, ArrowUpCircle, TrendingUp, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateOnly } from '@/utils/dateFormat';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { vendorPaymentsAPI, vendorsAPI } from '@/services/api';
import { VoidPaymentDialog } from '@/components/hq/VoidPaymentDialog';
import { VendorSuggestInput } from '@/components/hq/VendorSuggestInput';

const paymentModes = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'credit_note', label: 'Credit Note' }
];

// Grand totals / balances / payment amounts — rounded to nearest rupee, no paise
const inr = (v) => `₹${Math.round(Number(v) || 0).toLocaleString('en-IN')}`;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

const initialFormData = {
  vendor_id: '',
  payment_date: new Date().toISOString().split('T')[0],
  payment_mode: 'bank_transfer',
  amount: '',
  reference_number: '',
  bank_name: '',
  grn_ids: [],
  notes: ''
};

const VendorPaymentsPage = () => {
  const [activeTab, setActiveTab] = useState('payments');
  const [payments, setPayments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [ledgerSummaries, setLedgerSummaries] = useState([]);
  const [ledgerVendor, setLedgerVendor] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [ledgerDialogOpen, setLedgerDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedVendorLedger, setSelectedVendorLedger] = useState(null);
  const [ledgerData, setLedgerData] = useState(null);
  const [outstandingGRNs, setOutstandingGRNs] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchPayments = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (vendorFilter && vendorFilter !== 'all') params.vendor_id = vendorFilter;
      const response = await vendorPaymentsAPI.list(params);
      setPayments(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch payments', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, vendorFilter, toast]);

  const fetchVendors = async () => {
    try {
      const response = await vendorsAPI.list({ is_active: true });
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  const fetchLedgerSummaries = useCallback(async () => {
    try {
      const response = await vendorPaymentsAPI.ledgerSummary();
      setLedgerSummaries(response.data || []);
    } catch (error) {
      console.error('Failed to fetch ledger summaries:', error);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    fetchPayments();
    fetchVendors();
    fetchLedgerSummaries();
  }, [fetchPayments, fetchLedgerSummaries]);

  const handleOpenDialog = async () => {
    setFormData(initialFormData);
    setOutstandingGRNs([]);
    setDialogOpen(true);
  };

  const handleVendorSelect = async (vendorId) => {
    setFormData(prev => ({ ...prev, vendor_id: vendorId, grn_ids: [] }));
    if (vendorId) {
      try {
        const response = await vendorPaymentsAPI.getOutstandingGRNs(vendorId);
        setOutstandingGRNs(response.data);
      } catch (error) {
        console.error('Failed to fetch outstanding GRNs:', error);
        setOutstandingGRNs([]);
      }
    } else {
      setOutstandingGRNs([]);
    }
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setViewDialogOpen(true);
  };

  const handleViewLedger = async (vendor) => {
    setSelectedVendorLedger(vendor);
    setLedgerLoading(true);
    setLedgerData(null);
    setLedgerDialogOpen(true);
    try {
      const response = await vendorPaymentsAPI.getVendorLedger(vendor.id);
      setLedgerData(response.data);
    } catch (error) {
      setLedgerDialogOpen(false);
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to fetch ledger', variant: 'destructive' });
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleGRNToggle = (grnId) => {
    setFormData(prev => ({
      ...prev,
      grn_ids: prev.grn_ids.includes(grnId)
        ? prev.grn_ids.filter(id => id !== grnId)
        : [...prev.grn_ids, grnId]
    }));
  };

  const handleSave = async () => {
    if (!formData.vendor_id) {
      toast({ title: 'Validation Error', description: 'Please select a vendor', variant: 'destructive' });
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast({ title: 'Validation Error', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        payment_date: formData.payment_date ? new Date(formData.payment_date).toISOString() : null
      };

      await vendorPaymentsAPI.create(payload);
      toast({ title: 'Success', description: 'Payment recorded successfully' });
      setDialogOpen(false);
      fetchPayments(); fetchLedgerSummaries();
      fetchVendors(); // Refresh vendor balances
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const getPaymentModeLabel = (value) => {
    const mode = paymentModes.find(m => m.value === value);
    return mode?.label || value;
  };

  // Calculate total outstanding
  const totalOutstanding = ledgerSummaries.reduce((sum, v) => sum + Math.max(0, v.net_balance || 0), 0);
  const vendorsWithBalance = ledgerSummaries.filter(v => (v.net_balance || 0) > 0);
  const visibleLedgers = ledgerVendor ? ledgerSummaries.filter(v => v.id === ledgerVendor.id) : ledgerSummaries;

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Vendor Payments</h1>
          <p className="text-gray-500">Manage payments and view vendor ledgers</p>
        </div>
        <Button onClick={handleOpenDialog} className="bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-200" data-testid="record-payment-btn">
          <Plus className="w-4 h-4 mr-2" /> Record Payment
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Outstanding</p>
                <p className="text-2xl font-bold text-red-600">₹{Math.round(Number(totalOutstanding || 0)).toLocaleString('en-IN')}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Wallet className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Vendors with Balance</p>
                <p className="text-2xl font-bold text-amber-600">{vendorsWithBalance.length}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Recent Payments</p>
                <p className="text-2xl font-bold text-emerald-600">{payments.filter(p => p.status === 'completed').length}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 mb-6">
          <TabsTrigger value="payments" className="data-[state=active]:bg-white">Payments</TabsTrigger>
          <TabsTrigger value="ledgers" className="data-[state=active]:bg-white">Vendor Ledgers</TabsTrigger>
        </TabsList>

        <TabsContent value="payments">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search payment number, vendor, reference..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 bg-white border-gray-300" data-testid="payment-search-input" />
            </div>
            <div className="w-full sm:w-80">
              <VendorSuggestInput
                vendors={vendors}
                value={selectedVendor}
                onSelect={(v) => { setSelectedVendor(v); setVendorFilter(v ? v.id : ''); }}
                placeholder="Filter by vendor name..."
                testId="payment-vendor-filter"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : payments.length === 0 ? (
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardContent className="py-12 text-center">
                <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No payments found</p>
                <Button onClick={handleOpenDialog} variant="link" className="text-teal-600 mt-2">
                  Record your first payment
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white border-gray-200 shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-200">
                    <TableHead className="text-gray-600">Payment #</TableHead>
                    <TableHead className="text-gray-600">Vendor</TableHead>
                    <TableHead className="text-gray-600">Date</TableHead>
                    <TableHead className="text-gray-600">Mode</TableHead>
                    <TableHead className="text-gray-600">Reference</TableHead>
                    <TableHead className="text-gray-600 text-right">Amount</TableHead>
                    <TableHead className="text-gray-600">Status</TableHead>
                    <TableHead className="text-gray-600 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map(payment => (
                    <TableRow key={payment.id} className="border-gray-200 hover:bg-gray-50" data-testid={`payment-row-${payment.payment_number}`}>
                      <TableCell className="font-medium text-teal-600">{payment.payment_number}</TableCell>
                      <TableCell className="text-gray-700">{payment.vendor_name}</TableCell>
                      <TableCell className="text-gray-600">{formatDateOnly(payment.payment_date)}</TableCell>
                      <TableCell className="text-gray-600">{getPaymentModeLabel(payment.payment_mode)}</TableCell>
                      <TableCell className="text-gray-600">{payment.reference_number || '-'}</TableCell>
                      <TableCell className={`text-right font-bold ${payment.status === 'cancelled' ? 'text-gray-400 line-through' : 'text-emerald-600'}`}>₹{Math.round(Number(payment.amount || 0)).toLocaleString('en-IN')}</TableCell>
                      <TableCell>
                        <Badge className={statusConfig[payment.status]?.color}>
                          {statusConfig[payment.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleViewPayment(payment)}>
                            <Eye className="w-4 h-4 text-gray-500" />
                          </Button>
                          {payment.status === 'completed' && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Void payment" onClick={() => { setSelectedPayment(payment); setCancelDialogOpen(true); }} data-testid={`void-payment-${payment.payment_number}`}>
                              <Ban className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="ledgers">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="w-full sm:w-96">
              <VendorSuggestInput
                vendors={ledgerSummaries}
                value={ledgerVendor}
                onSelect={setLedgerVendor}
                placeholder="Find vendor ledger by name or code..."
                testId="ledger-vendor-filter"
              />
            </div>
            <p className="text-sm text-gray-500 self-center">{visibleLedgers.length} vendor(s) · balances include GRNs, payments, voids and returns</p>
          </div>
          <Card className="bg-white border-gray-200 shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200">
                  <TableHead className="text-gray-600">Vendor</TableHead>
                  <TableHead className="text-gray-600">Code</TableHead>
                  <TableHead className="text-gray-600">Payment Terms</TableHead>
                  <TableHead className="text-gray-600 text-right">Opening</TableHead>
                  <TableHead className="text-gray-600 text-right">Purchases</TableHead>
                  <TableHead className="text-gray-600 text-right">Paid</TableHead>
                  <TableHead className="text-gray-600 text-right">Returns</TableHead>
                  <TableHead className="text-gray-600 text-right">Balance</TableHead>
                  <TableHead className="text-gray-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleLedgers.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-gray-500">No vendors found</TableCell></TableRow>
                )}
                {visibleLedgers.map(vendor => (
                  <TableRow key={vendor.id} className="border-gray-200 hover:bg-gray-50" data-testid={`ledger-row-${vendor.code}`}>
                    <TableCell className="font-medium text-gray-800">{vendor.name}</TableCell>
                    <TableCell className="text-teal-600">{vendor.code}</TableCell>
                    <TableCell className="text-gray-600">{vendor.payment_terms?.replace('_', ' ').replace('credit', 'Credit')}</TableCell>
                    <TableCell className="text-right text-gray-600">{inr(vendor.opening_balance)}</TableCell>
                    <TableCell className="text-right text-gray-700">{inr(vendor.total_purchases)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{inr(vendor.total_payments)}</TableCell>
                    <TableCell className="text-right text-blue-700">{inr(vendor.total_returns)}</TableCell>
                    <TableCell className={`text-right font-bold ${vendor.net_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid={`ledger-balance-${vendor.code}`}>
                      {inr(Math.abs(vendor.net_balance || 0))}
                      {vendor.net_balance > 0 ? ' (Payable)' : vendor.net_balance < 0 ? ' (Advance)' : ''}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => handleViewLedger(vendor)} className="border-gray-300 text-gray-600" data-testid={`view-ledger-${vendor.code}`}>
                        View Ledger
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Record Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Record Payment</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label className="text-gray-700">Vendor *</Label>
                <Select value={formData.vendor_id} onValueChange={handleVendorSelect}>
                  <SelectTrigger className="bg-gray-50 border-gray-300" data-testid="record-vendor-select"><SelectValue placeholder="Select Vendor" /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-60">
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name} - Balance: {inr(v.current_balance)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Payment Date *</Label>
                <Input type="date" value={formData.payment_date}
                  onChange={(e) => setFormData(p => ({ ...p, payment_date: e.target.value }))}
                  className="bg-gray-50 border-gray-300" />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Payment Mode *</Label>
                <Select value={formData.payment_mode} onValueChange={(v) => setFormData(p => ({ ...p, payment_mode: v }))}>
                  <SelectTrigger className="bg-gray-50 border-gray-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {paymentModes.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Amount (₹) *</Label>
                <Input type="number" min="0" step="0.01" value={formData.amount}
                  onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00" className="bg-gray-50 border-gray-300" data-testid="record-amount-input" />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Reference Number</Label>
                <Input value={formData.reference_number}
                  onChange={(e) => setFormData(p => ({ ...p, reference_number: e.target.value }))}
                  placeholder="Cheque No / UTR / Txn ID" className="bg-gray-50 border-gray-300" />
              </div>

              {(formData.payment_mode === 'bank_transfer' || formData.payment_mode === 'cheque') && (
                <div className="space-y-2 col-span-2">
                  <Label className="text-gray-700">Bank Name</Label>
                  <Input value={formData.bank_name}
                    onChange={(e) => setFormData(p => ({ ...p, bank_name: e.target.value }))}
                    placeholder="Bank name" className="bg-gray-50 border-gray-300" />
                </div>
              )}
            </div>

            {/* Outstanding GRNs */}
            {outstandingGRNs.length > 0 && (
              <div className="space-y-2">
                <Label className="text-gray-700">Link to GRNs (Optional)</Label>
                <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                  {outstandingGRNs.map(grn => (
                    <div key={grn.id} 
                      className={`px-3 py-2 border-b border-gray-100 last:border-0 flex items-center justify-between cursor-pointer hover:bg-gray-50 ${formData.grn_ids.includes(grn.id) ? 'bg-teal-50' : ''}`}
                      onClick={() => handleGRNToggle(grn.id)}>
                      <div>
                        <span className="font-medium text-gray-800">{grn.grn_number}</span>
                        <span className="text-gray-500 text-sm ml-2">{grn.invoice_number || 'No Invoice'}</span>
                      </div>
                      <span className="font-medium text-gray-700">{inr(grn.grand_total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-gray-700">Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                placeholder="Additional notes..." className="bg-gray-50 border-gray-300" rows={2} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300 text-gray-600">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="record-save-btn">
              {saving ? 'Saving...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Payment Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-3">
              Payment: {selectedPayment?.payment_number}
              {selectedPayment && (
                <Badge className={statusConfig[selectedPayment.status]?.color}>
                  {statusConfig[selectedPayment.status]?.label}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedPayment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Vendor</p>
                  <p className="font-medium text-gray-800">{selectedPayment.vendor_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Amount</p>
                  <p className="font-bold text-emerald-600 text-lg">{inr(selectedPayment.amount)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Payment Date</p>
                  <p className="font-medium text-gray-800">{formatDateOnly(selectedPayment.payment_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Payment Mode</p>
                  <p className="font-medium text-gray-800">{getPaymentModeLabel(selectedPayment.payment_mode)}</p>
                </div>
                {selectedPayment.reference_number && (
                  <div>
                    <p className="text-gray-500">Reference</p>
                    <p className="font-medium text-gray-800">{selectedPayment.reference_number}</p>
                  </div>
                )}
                {selectedPayment.bank_name && (
                  <div>
                    <p className="text-gray-500">Bank</p>
                    <p className="font-medium text-gray-800">{selectedPayment.bank_name}</p>
                  </div>
                )}
              </div>
              {selectedPayment.notes && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-gray-500 text-sm">Notes</p>
                  <p className="text-gray-700">{selectedPayment.notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="border-gray-300">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ledger Dialog */}
      <Dialog open={ledgerDialogOpen} onOpenChange={setLedgerDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">
              Vendor Ledger: {selectedVendorLedger?.name} <span className="text-sm font-normal text-teal-600">{selectedVendorLedger?.code}</span>
            </DialogTitle>
          </DialogHeader>

          {ledgerLoading && <div className="py-10 text-center text-gray-500" data-testid="ledger-loading">Loading ledger...</div>}

          {ledgerData && (
            <div className="space-y-6" data-testid="ledger-dialog-content">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-gray-500">Purchases (GRN)</p>
                    <p className="text-xl font-bold text-red-600">{inr(ledgerData.summary?.total_purchases)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-gray-500">Payments</p>
                    <p className="text-xl font-bold text-emerald-600">{inr(ledgerData.summary?.total_payments)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-gray-500">Returns / Credits</p>
                    <p className="text-xl font-bold text-amber-600">{inr(ledgerData.summary?.total_returns)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-sm text-gray-500">Net Balance</p>
                    <p className={`text-xl font-bold ${ledgerData.summary?.net_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="ledger-net-balance">
                      {inr(Math.abs(ledgerData.summary?.net_balance || 0))}
                      {ledgerData.summary?.net_balance > 0 ? ' (Payable)' : ledgerData.summary?.net_balance < 0 ? ' (Advance)' : ''}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Ledger Entries */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 border-gray-200">
                      <TableHead className="text-gray-600">Date</TableHead>
                      <TableHead className="text-gray-600">Type</TableHead>
                      <TableHead className="text-gray-600">Reference</TableHead>
                      <TableHead className="text-gray-600 text-right">Purchase (Cr)</TableHead>
                      <TableHead className="text-gray-600 text-right">Payment (Dr)</TableHead>
                      <TableHead className="text-gray-600 text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledgerData.entries?.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">No transactions yet for this vendor</TableCell></TableRow>
                    )}
                    {ledgerData.entries?.map((entry, i) => (
                      <TableRow key={entry.id || i} className="border-gray-200" data-testid={`ledger-entry-${entry.reference_number || i}`}>
                        <TableCell className="text-gray-600 whitespace-nowrap">{formatDateOnly(entry.entry_date)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {entry.entry_type === 'purchase' && <ArrowDownCircle className="w-4 h-4 text-red-500" />}
                            {entry.entry_type === 'payment' && <ArrowUpCircle className="w-4 h-4 text-emerald-500" />}
                            {entry.entry_type === 'payment_reversal' && <XCircle className="w-4 h-4 text-amber-500" />}
                            <div>
                              <span className="text-gray-700 capitalize">{entry.entry_type?.replace(/_/g, ' ')}</span>
                              {entry.description && <p className="text-xs text-gray-500 max-w-[260px] truncate" title={entry.description}>{entry.description}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-teal-600 whitespace-nowrap">{entry.reference_number || '-'}</TableCell>
                        <TableCell className="text-right text-red-600 font-medium">
                          {entry.credit_amount > 0 ? inr(entry.credit_amount) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-emerald-600 font-medium">
                          {entry.debit_amount > 0 ? inr(entry.debit_amount) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium text-gray-800">
                          {inr(entry.running_balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Opening Balance (oldest, shown last since list is newest first) */}
                    <TableRow className="border-gray-200 bg-gray-50">
                      <TableCell className="text-gray-600">-</TableCell>
                      <TableCell className="text-gray-700 font-medium">Opening Balance</TableCell>
                      <TableCell className="text-gray-500">-</TableCell>
                      <TableCell className="text-right">-</TableCell>
                      <TableCell className="text-right">-</TableCell>
                      <TableCell className="text-right font-medium text-gray-800">
                        {inr(ledgerData.vendor?.opening_balance)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setLedgerDialogOpen(false)} className="border-gray-300">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VoidPaymentDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        payment={selectedPayment}
        onDone={() => { fetchPayments(); fetchVendors(); fetchLedgerSummaries(); }}
      />
    </div>
  );
};

export default VendorPaymentsPage;
